// A list of documents with preview, download and new-version actions, plus a drop
// zone: dropping a file opens the upload dialog with the attestation.
import { useState, type DragEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Download,
  Eye,
  File,
  FileImage,
  FileSpreadsheet,
  FileText,
  History,
  Lock,
  SquareArrowOutUpRight,
  Trash2,
  Upload,
} from 'lucide-react';
import { useMe } from '@/lib/auth';
import { errorMessage } from '@/lib/api';
import { canPreview, docTypeLabel, downloadVersion, fmtBytes, versionLink, type Doc } from '@/lib/documents';
import type { Program } from '@/lib/types';
import { cn, fmtDate, fmtRelative } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge, Spinner } from '@/components/ui/misc';
import { ConfirmDialog, RESTORE_NOTE } from '@/components/ui/confirm';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Dialog } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { UploadDialog, type UploadTarget } from './UploadDialog';

export function DocIcon({ mime, className }: { mime: string | undefined; className?: string }) {
  const I = !mime
    ? File
    : mime === 'application/pdf'
      ? FileText
      : mime.startsWith('image/')
        ? FileImage
        : /sheet|csv/.test(mime)
          ? FileSpreadsheet
          : File;
  return <I className={cn('size-4 shrink-0 text-muted', className)} />;
}

export function useDocActions(programId: string) {
  const toast = useToast();
  const [preview, setPreview] = useState<{ url: string; mime: string; title: string } | null>(null);
  const open = async (d: Pick<Doc, 'id' | 'title'>, v: { version: number; mimeType: string }) => {
    try {
      if (canPreview(v.mimeType))
        setPreview({ url: await versionLink(programId, d.id, v.version, 'inline'), mime: v.mimeType, title: d.title });
      else await downloadVersion(programId, d.id, v.version);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  const download = (d: Pick<Doc, 'id'>, version: number) =>
    downloadVersion(programId, d.id, version).catch((e) => toast.error(errorMessage(e)));
  const previewDialog = (
    <Dialog
      open={Boolean(preview)}
      onClose={() => setPreview(null)}
      title={preview?.title ?? ''}
      description="Link expires in five minutes."
      wide
    >
      {preview &&
        (preview.mime === 'application/pdf' ? (
          <iframe
            src={preview.url}
            title={preview.title}
            className="h-[72vh] w-full rounded border border-border bg-white"
          />
        ) : (
          <img src={preview.url} alt={preview.title} className="mx-auto max-h-[72vh] rounded border border-border" />
        ))}
    </Dialog>
  );
  return { open, download, previewDialog };
}

export function DocumentList({
  program,
  docs,
  loading,
  uploadTarget,
  canUpload,
  emptyText = 'No documents yet.',
  showContext,
}: {
  program: Program;
  docs: Doc[] | undefined;
  loading?: boolean;
  uploadTarget?: UploadTarget;
  canUpload?: boolean;
  emptyText?: string;
  showContext?: (d: Doc) => ReactNode;
}) {
  const { open, download, previewDialog } = useDocActions(program.id);
  const [upload, setUpload] = useState<{ target: UploadTarget; file: File | null } | null>(null);
  const [history, setHistory] = useState<Doc | null>(null);
  const [over, setOver] = useState(false);
  const isAdmin = program.capabilities.includes('plan.manage');
  const me = useMe().data?.user.id;
  const qc = useQueryClient();
  const toast = useToast();
  const [deleting, setDeleting] = useState<Doc | null>(null);
  const remove = useMutation({
    mutationFn: (d: Doc) => api.delete(`/api/programs/${program.id}/documents/${d.id}`),
    onSuccess: () => {
      setDeleting(null);
      void qc.invalidateQueries({ queryKey: ['documents', program.id] });
      void qc.invalidateQueries({ queryKey: ['tracker', program.id] });
    },
    onError: (e) => {
      setDeleting(null);
      toast.error(errorMessage(e));
    },
  });

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    if (canUpload && uploadTarget && e.dataTransfer.files[0])
      setUpload({ target: uploadTarget, file: e.dataTransfer.files[0] });
  };

  return (
    <div
      onDragOver={(e) => {
        if (!canUpload) return;
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      className={cn('rounded-md transition-colors', over && 'bg-brand-soft/40 ring-2 ring-ring/40')}
    >
      {loading ? (
        <Spinner className="py-6" />
      ) : docs?.length ? (
        <ul className="flex flex-col">
          {docs.map((d) => (
            <li key={d.id} className="flex items-center gap-2.5 border-b border-border py-2 text-[13px] last:border-0">
              <DocIcon mime={d.current?.mimeType} />
              <div className="min-w-0 flex-1">
                <button
                  className="block max-w-full truncate text-left font-medium hover:underline"
                  onClick={() => d.current && open(d, d.current)}
                >
                  {d.title}
                </button>
                <p className="truncate text-xs text-muted">
                  {docTypeLabel(d.type, program)}
                  {d.current &&
                    ` · v${d.current.version} · ${fmtBytes(d.current.sizeBytes)} · ${d.current.uploadedBy}, ${fmtRelative(d.current.uploadedAt)}`}
                  {showContext?.(d)}
                </p>
              </div>
              {d.internal && (
                <Badge tone="warn">
                  <Lock className="size-3" /> Internal
                </Badge>
              )}
              {d.current && canPreview(d.current.mimeType) && (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Preview ${d.title}`}
                  onClick={() => open(d, d.current!)}
                >
                  <Eye />
                </Button>
              )}
              {d.current && (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Download ${d.title}`}
                  onClick={() => download(d, d.current!.version)}
                >
                  <Download />
                </Button>
              )}
              {d.versions.length > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Version history of ${d.title}`}
                  onClick={() => setHistory(d)}
                >
                  <History />
                </Button>
              )}
              <Link
                to={`/p/${program.id}/documents/${d.id}`}
                aria-label={`Open ${d.title}`}
                title="Open the document page"
                className="inline-flex size-8 items-center justify-center rounded-md text-muted hover:bg-surface-2 hover:text-text [&_svg]:size-4"
              >
                <SquareArrowOutUpRight />
              </Link>
              {isAdmin && d.type !== 'PUBLISHED_REPORT' && (
                <Button variant="ghost" size="icon" aria-label={`Delete ${d.title}`} onClick={() => setDeleting(d)}>
                  <Trash2 />
                </Button>
              )}
              {canUpload && (isAdmin || (d.type !== 'DELIVERABLE' && d.createdById === me)) && (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`File a new version of ${d.title}`}
                  onClick={() => setUpload({ target: { documentId: d.id, label: d.title }, file: null })}
                >
                  <Upload />
                </Button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="py-2 text-[13px] text-muted">{emptyText}</p>
      )}
      <ConfirmDialog
        open={!!deleting}
        title="Delete this document?"
        description={`${deleting?.title ?? ''}, with every version. ${RESTORE_NOTE}`}
        onConfirm={() => deleting && remove.mutate(deleting)}
        onClose={() => setDeleting(null)}
        loading={remove.isPending}
      />
      {canUpload && uploadTarget && (
        <button
          type="button"
          onClick={() => setUpload({ target: uploadTarget, file: null })}
          className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-border-strong py-2 text-xs text-muted hover:border-ring hover:text-text"
        >
          <Upload className="size-3.5" /> Drop a file here or click to file one
        </button>
      )}

      <UploadDialog
        program={program}
        target={upload?.target ?? {}}
        initialFile={upload?.file}
        open={Boolean(upload)}
        onClose={() => setUpload(null)}
      />
      {previewDialog}
      <Dialog
        open={Boolean(history)}
        onClose={() => setHistory(null)}
        title={`Versions of ${history?.title ?? ''}`}
        description="Every version is kept; superseded versions stay downloadable."
      >
        <ul className="flex flex-col">
          {history?.versions.map((v) => (
            <li
              key={v.version}
              className="flex items-center gap-3 border-b border-border py-2 text-[13px] last:border-0"
            >
              <span className="w-10 font-medium tabular">v{v.version}</span>
              <span className="min-w-0 flex-1 truncate">
                {v.fileName}
                <span className="block text-xs text-muted">
                  {v.uploadedBy}, {fmtDate(v.uploadedAt.slice(0, 10))} · {fmtBytes(v.sizeBytes)}
                  {v.superseded && ' · superseded'}
                </span>
              </span>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Download version ${v.version}`}
                onClick={() => history && download(history, v.version)}
              >
                <Download />
              </Button>
            </li>
          ))}
        </ul>
      </Dialog>
    </div>
  );
}
